// Cross-platform fullscreen for a playing <video>. Exported for Mode B
// (bring-your-own-player) integrators and shared by the managed player API
// and the custom control bar, so the platform quirks live in exactly one
// place.
//
// Capability ladder, because the element Fullscreen API is not universal:
//  1. Standard element fullscreen on `container` (or the video itself) — a
//     container keeps custom DOM controls/overlays on screen.
//  2. Prefixed element fullscreen — older iPad/macOS Safari (< 16.4) ships
//     only webkitRequestFullscreen / webkitExitFullscreen.
//  3. Video-native fullscreen — iPhone Safari has NEVER shipped the element
//     API in any form (`requestFullscreen` is undefined on every element),
//     so the only fullscreen there is the AVPlayer-style
//     `video.webkitEnterFullscreen()` with NATIVE controls (the YouTube
//     behavior). Custom DOM is not visible inside it, it requires loaded
//     media (a pre-play call is a no-op), and it must run from a user
//     gesture.

type FullscreenTarget = HTMLElement & {
  requestFullscreen?: () => Promise<void>;
  webkitRequestFullscreen?: () => void;
};

type FullscreenDocument = Document & {
  fullscreenElement?: Element | null;
  exitFullscreen?: () => Promise<void>;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
};

type WebkitVideo = HTMLVideoElement & {
  webkitSupportsFullscreen?: boolean;
  webkitDisplayingFullscreen?: boolean;
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
};

function ownerDoc(video: HTMLVideoElement): FullscreenDocument {
  return (video.ownerDocument ?? document) as FullscreenDocument;
}

/** The element currently in element fullscreen (unprefixed or prefixed), if any. */
function fullscreenElement(doc: FullscreenDocument): Element | null {
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/**
 * True while this video's fullscreen presentation is active: element
 * fullscreen on `container` or the video itself, or iPhone's video-native
 * fullscreen. Pass the same `container` you pass to enterVideoFullscreen.
 */
export function videoFullscreenActive(video: HTMLVideoElement, container?: HTMLElement): boolean {
  const el = fullscreenElement(ownerDoc(video));
  if (el != null && (el === video || (container != null && el === container))) return true;
  return (video as WebkitVideo).webkitDisplayingFullscreen === true;
}

/**
 * Enter fullscreen for `video`, walking the capability ladder above.
 * `container` is the element to present in element fullscreen (defaults to
 * the video) — pass your player wrapper to keep your own controls visible on
 * platforms that support it. Returns false when NO mechanism exists (some
 * webviews) so the caller can hide or disable its button; rejections from a
 * denied request (permissions policy) propagate.
 *
 * Call from a user gesture: every branch of the ladder requires one.
 */
export async function enterVideoFullscreen(
  video: HTMLVideoElement,
  container?: HTMLElement,
): Promise<boolean> {
  const target = (container ?? video) as FullscreenTarget;
  if (typeof target.requestFullscreen === 'function') {
    await target.requestFullscreen();
    return true;
  }
  if (typeof target.webkitRequestFullscreen === 'function') {
    target.webkitRequestFullscreen();
    return true;
  }
  const v = video as WebkitVideo;
  if (v.webkitSupportsFullscreen && typeof v.webkitEnterFullscreen === 'function') {
    v.webkitEnterFullscreen();
    return true;
  }
  return false;
}

/**
 * Leave whichever fullscreen presentation `video` is in: iPhone's video-native
 * fullscreen first (it reports only through the video, never the document),
 * then element fullscreen via the document (unprefixed or prefixed). Safe to
 * call when not fullscreen — and scoped to THIS video's presentation: an
 * unrelated element's fullscreen (a lightbox, another player) is left alone,
 * mirroring the ownership check exitPictureInPicture makes on
 * pictureInPictureElement. Pass the same `container` you entered with.
 */
export async function exitVideoFullscreen(
  video: HTMLVideoElement,
  container?: HTMLElement,
): Promise<void> {
  const v = video as WebkitVideo;
  if (v.webkitDisplayingFullscreen) {
    v.webkitExitFullscreen?.();
    return;
  }
  const doc = ownerDoc(video);
  const el = fullscreenElement(doc);
  if (el != null && (el === video || (container != null && el === container))) {
    if (typeof doc.exitFullscreen === 'function') await doc.exitFullscreen();
    else doc.webkitExitFullscreen?.();
  }
}
